import React, { useMemo } from 'react';
import { Card, Col, Row, Space, Tag, Typography } from 'antd';
import {
  HeroKpiCard,
  MicroKpiCard,
  OiEmptyState,
  PrimaryKpiCard,
  SecondaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

type BriefMetric = {
  key?: string;
  label?: string;
  value?: number | string | null;
  unit?: string;
};

type BriefSection = {
  key?: string;
  title?: string;
  available?: boolean;
  metrics?: BriefMetric[];
  score?: number;
  statusLabel?: string;
  dimensions?: Record<string, number>;
};

type PriorityChip = {
  priority?: string;
  headline?: string;
  domain?: string;
};

function parseBody(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  return null;
}

function formatMetricValue(m: BriefMetric): string {
  if (m.value == null || m.value === '') return '—';
  if (m.unit === 'amt' && typeof m.value === 'number') {
    return formatMoney(m.value);
  }
  if (m.unit === '%' && typeof m.value === 'number') {
    return `${m.value}%`;
  }
  if (typeof m.value === 'number') {
    return Number.isInteger(m.value) ? String(m.value) : m.value.toLocaleString();
  }
  return String(m.value);
}

function priorityColor(p?: string): string {
  if (p === 'critical' || p === 'high') return 'red';
  if (p === 'medium') return 'orange';
  return 'blue';
}

const SECTION_ORDER = [
  'projects',
  'estimation',
  'cash',
  'sales',
  'operations',
  'approvals',
] as const;

export function EoipMorningBriefPanel({
  brief,
}: {
  brief: Record<string, unknown> | null | undefined;
}) {
  const body = useMemo(() => {
    if (!brief) return null;
    return parseBody(brief.body_json ?? brief.bodyJson ?? brief);
  }, [brief]);

  const sections = (body?.sections || {}) as Record<string, BriefSection>;
  const pulseSection = sections.pulse;
  const pulse =
    pulseSection ||
    (body?.pulse as BriefSection | undefined) ||
    (brief?.pulse_score != null
      ? {
          score: Number(brief.pulse_score),
          statusLabel: String(brief.status_label || ''),
          dimensions: {},
        }
      : null);

  const priorities = (Array.isArray(body?.priorities) ? body!.priorities : []) as PriorityChip[];
  const summary = String(brief?.summary || '');
  const hasStructured = Boolean(body?.sections && typeof body.sections === 'object');

  if (!brief) {
    return (
      <OiEmptyState title="No brief for this role/date" body="Briefs generate with the morning EOIP cron." />
    );
  }

  if (!hasStructured) {
    return (
      <div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Legacy brief — regenerate via EOIP morning cron for stats layout.
        </Text>
        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
          {summary.slice(0, 800) || JSON.stringify(body || brief, null, 2).slice(0, 800)}
        </pre>
      </div>
    );
  }

  const dimEntries = Object.entries(pulse?.dimensions || {}).slice(0, 8);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Text type="secondary">{summary}</Text>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={8}>
          <HeroKpiCard
            label="Enterprise Pulse"
            value={pulse?.score != null ? pulse.score : '—'}
            score={typeof pulse?.score === 'number' ? pulse.score : undefined}
            sublabel={pulse?.statusLabel || undefined}
          />
        </Col>
        {dimEntries.map(([k, v]) => (
          <Col xs={12} sm={8} md={4} key={k}>
            <MicroKpiCard label={k} value={typeof v === 'number' ? v : '—'} />
          </Col>
        ))}
      </Row>

      {SECTION_ORDER.map((key) => {
        const sec = sections[key];
        if (!sec || sec.available === false) return null;
        const metrics = Array.isArray(sec.metrics) ? sec.metrics : [];
        if (!metrics.length) return null;
        return (
          <Card key={key} size="small" title={sec.title || key} bordered={false}>
            <Row gutter={[12, 12]}>
              {metrics.map((m, i) => (
                <Col xs={12} sm={8} md={6} lg={4} key={m.key || `${key}-${i}`}>
                  {i === 0 ? (
                    <PrimaryKpiCard label={m.label || m.key || '—'} value={formatMetricValue(m)} />
                  ) : (
                    <SecondaryKpiCard label={m.label || m.key || '—'} value={formatMetricValue(m)} />
                  )}
                </Col>
              ))}
            </Row>
          </Card>
        );
      })}

      <Card size="small" title="Priorities" bordered={false}>
        {priorities.length === 0 ? (
          <Text type="secondary">No critical priorities</Text>
        ) : (
          <Space wrap size={[8, 8]}>
            {priorities.slice(0, 5).map((p, i) => (
              <Tag key={i} color={priorityColor(p.priority)}>
                {p.domain ? `${p.domain}: ` : ''}
                {p.headline || '—'}
              </Tag>
            ))}
          </Space>
        )}
      </Card>
    </Space>
  );
}

export default EoipMorningBriefPanel;
